import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  GridActionsCellItem,
  GridColumns,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  useCanEditNoteCategories,
  useNoteCategories,
} from '@dispatch/shared/hooks';
import {
  createNoteCategory,
  NoteCategoryDto,
  renameNoteCategory,
  retireNoteCategory,
} from '@dispatch/shared/services';
import {
  actionsColumn,
  DEFAULT_NOTE_CATEGORY_IDS,
  textColumn,
} from '@dispatch/web/ui';
import { useCurrentUser } from '@dispatch/web/auth';
import { AdminDashboardTable } from './AdminDashboardTable';

const extractConflictMessage = (error: unknown, fallback: string): string => {
  const data =
    (error as { response?: { data?: unknown } })?.response?.data ?? undefined;
  if (typeof data === 'string' && data.trim().length > 0) return data;
  if (data && typeof data === 'object') {
    const message =
      (data as { message?: unknown }).message ??
      (data as { error?: unknown }).error ??
      (data as { detail?: unknown }).detail;
    if (typeof message === 'string' && message.trim().length > 0)
      return message;
  }
  return fallback;
};

const errorStatus = (error: unknown): number =>
  (error as { response?: { status?: number } })?.response?.status ?? 0;

interface EditModalProps {
  open: boolean;
  existing?: NoteCategoryDto;
  onClose: () => void;
  onSaved: () => void;
}

const NoteCategoryEditModal: React.FC<EditModalProps> = ({
  open,
  existing,
  onClose,
  onSaved,
}) => {
  const isEdit = !!existing;
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset when the modal opens or the target row changes.
  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setError(null);
    }
  }, [open, existing]);

  // Focus the name input as soon as the dialog mounts.
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [open]);

  const trimmed = name.trim();
  const unchanged = isEdit && trimmed === (existing?.name ?? '');
  const saveDisabled = saving || !trimmed || unchanged;

  const handleSave = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (existing) {
        await renameNoteCategory(existing.categoryId, trimmed);
      } else {
        await createNoteCategory(trimmed);
      }
      onSaved();
      onClose();
    } catch (e) {
      if (errorStatus(e) === 409) {
        setError(
          extractConflictMessage(
            e,
            `The name "${trimmed}" is already used by another category in this tenant`
          )
        );
      } else {
        setError(
          isEdit ? 'Failed to rename category' : 'Failed to create category'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      data-testid="note-category-edit-modal"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {isEdit ? 'Edit note category' : 'Add note category'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ paddingTop: 1 }}>
          <TextField
            size="small"
            label="Name"
            value={name}
            inputRef={inputRef}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !saveDisabled) {
                e.preventDefault();
                void handleSave();
              }
            }}
            error={!!error}
            helperText={error || undefined}
            data-testid="note-category-edit-name"
            inputProps={{ 'data-testid': 'note-category-edit-name-input' }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={saving}
          data-testid="note-category-edit-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saveDisabled}
          data-testid="note-category-edit-save"
        >
          {saving ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteConfirmProps {
  open: boolean;
  category?: NoteCategoryDto;
  onClose: () => void;
  onDeleted: () => void;
}

const NoteCategoryDeleteConfirm: React.FC<DeleteConfirmProps> = ({
  open,
  category,
  onClose,
  onDeleted,
}) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!category || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await retireNoteCategory(category.categoryId);
      onDeleted();
      onClose();
    } catch (e) {
      setError('Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      data-testid="note-category-delete-modal"
    >
      <DialogTitle>Delete note category?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will remove &quot;{category?.name}&quot; from new-note dropdowns
          but existing notes referencing it will continue to display the name.
          Continue?
        </DialogContentText>
        {error && (
          <DialogContentText
            color="error"
            sx={{ marginTop: 1 }}
            data-testid="note-category-delete-error"
          >
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={deleting}
          data-testid="note-category-delete-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting}
          data-testid="note-category-delete-confirm"
        >
          {deleting ? <CircularProgress size={16} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * NoteCategoryAdmin - admin section for managing tenant note categories.
 * Renders inside CompanyAdminDetails as another `AdminDashboardTable`,
 * matching the table chrome (Add button top-right, 3-dots row menu)
 * used by the sibling admin sections.
 *
 * Columns: Name / Status (active vs inactive icon) / Actions.
 * "Delete" is a soft-retire on the wire (the API endpoint is still
 * `retireNoteCategory`); retired rows render with the red inactive
 * status icon and have the row Delete action disabled. The 5 platform
 * defaults are also locked from delete via DEFAULT_NOTE_CATEGORY_IDS
 * with a tooltip explaining why.
 *
 * Returns null when the user lacks `edit note-categories`.
 */
export const NoteCategoryAdmin: React.FC = () => {
  const { tenantId } = useCurrentUser();
  const canEdit = useCanEditNoteCategories();
  const queryClient = useQueryClient();

  // includeRetired is always true here — the Status column is what
  // surfaces active vs. inactive now, no checkbox needed.
  const { data, isLoading } = useNoteCategories(tenantId, true);

  const [editState, setEditState] = useState<{
    open: boolean;
    existing?: NoteCategoryDto;
  }>({ open: false });
  const [deleteState, setDeleteState] = useState<{
    open: boolean;
    category?: NoteCategoryDto;
  }>({ open: false });

  if (!canEdit) return null;

  const categories = data ?? [];

  const refresh = () => {
    if (!tenantId) return;
    queryClient.invalidateQueries(['note-categories', tenantId, true]);
    queryClient.invalidateQueries(['note-categories', tenantId, false]);
  };

  const handleAddClick = () => setEditState({ open: true });
  const handleEditClick = (category: NoteCategoryDto) =>
    setEditState({ open: true, existing: category });
  const handleDeleteClick = (category: NoteCategoryDto) =>
    setDeleteState({ open: true, category });

  const columns: GridColumns = [
    textColumn({
      field: 'name',
      headerName: 'Name',
      flex: 2,
    }),
    {
      field: 'status',
      headerName: 'Active',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      valueGetter: (params) => (params.row.isRetired ? 'Inactive' : 'Active'),
      renderCell: (params: GridRenderCellParams) =>
        params.row.isRetired ? (
          <Tooltip title="Inactive">
            <CancelIcon
              color="error"
              fontSize="small"
              data-testid={`note-category-status-inactive-${params.row.categoryId}`}
              aria-label="Inactive"
            />
          </Tooltip>
        ) : (
          <Tooltip title="Active">
            <CheckCircleIcon
              color="success"
              fontSize="small"
              data-testid={`note-category-status-active-${params.row.categoryId}`}
              aria-label="Active"
            />
          </Tooltip>
        ),
    },
    actionsColumn((params: GridRowParams) => {
      const row = params.row as NoteCategoryDto;
      const isDefault = DEFAULT_NOTE_CATEGORY_IDS.has(row.categoryId);
      const deleteDisabled = isDefault || !!row.isRetired;
      const deleteLabel = isDefault
        ? "Default categories can't be deleted — rename instead."
        : row.isRetired
        ? 'Already deleted'
        : 'Delete';
      return [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="EDIT"
          onClick={() => handleEditClick(row)}
          showInMenu
          data-testid={`note-category-edit-action-${row.categoryId}`}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label={deleteDisabled ? `DELETE — ${deleteLabel}` : 'DELETE'}
          onClick={() => handleDeleteClick(row)}
          showInMenu
          disabled={deleteDisabled}
          data-testid={`note-category-delete-action-${row.categoryId}`}
        />,
      ];
    }),
  ];

  return (
    <>
      <AdminDashboardTable<NoteCategoryDto>
        type="note-category"
        cardTitle="Note Categories"
        buttonTitle="Add Note Category"
        identifier="categoryId"
        columns={columns}
        data={categories}
        isLoading={isLoading}
        handleClick={handleAddClick}
      />
      <NoteCategoryEditModal
        open={editState.open}
        existing={editState.existing}
        onClose={() => setEditState({ open: false })}
        onSaved={refresh}
      />
      <NoteCategoryDeleteConfirm
        open={deleteState.open}
        category={deleteState.category}
        onClose={() => setDeleteState({ open: false })}
        onDeleted={refresh}
      />
    </>
  );
};
